import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useHistory, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { Application } from '@feathersjs/feathers';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Stack from '@mui/material/Stack';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TablePagination from '@mui/material/TablePagination';
import Checkbox from '@mui/material/Checkbox';
import Paper from '@mui/material/Paper';
import LinearProgress from '@mui/material/LinearProgress';
import CircularProgress from '@mui/material/CircularProgress';
import Link from '@mui/material/Link';
import { DataDetailToolbar, LoadingSpinner } from '../../components';
import { useFeathers } from '../../app/util';
import { ServiceTypes } from '../../app/feathers/ServiceTypes';
import { Election } from '../../app/feathers/elections/Election';
import DataOperationsTable from '../UploadCandidateList/DataOperationsTable';
import {
  ElectionMigrationEntity,
  ElectionMigrationDataOperation,
  ElectionMigrationRun,
  ElectionMigrationTargetRaceDisplay
} from '../../app/feathers/election-migration-runs/ElectionMigrationRun';
import { DataOperation } from '../../app/feathers/candidate-lists/CandidateList';
import {
  WinnersPickerCell,
  getSelectedCandidateIdsFromEntity,
  isEntityWinnerSelectionIncomplete,
  toggleWinnerSelection
} from './CandidateWinnerRows';

const MAX_MODAL_CANDIDATES_VISIBLE = 5;

const toTableDataOperation = (operation: ElectionMigrationDataOperation): DataOperation => ({
  ...operation,
  operationType: operation.operationType === 'no-op' ? 'update' : operation.operationType,
  data: operation.data ?? {}
});

const toElectionMigrationDataOperation = (operation: DataOperation): ElectionMigrationDataOperation => ({
  ...operation,
  entityType: operation.entityType as ElectionMigrationDataOperation['entityType']
});

const Wrapper = styled.div`
  width: calc(100% - 2 * 24px);
  display: flex;
  flex-direction: column;
  padding: 24px;
  align-items: stretch;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 24px;
`;

function TargetRaceInfoCell({ display, fallbackTitle }: { display?: ElectionMigrationTargetRaceDisplay; fallbackTitle?: string }) {
  const officeLine =
    display?.officeName != null && display.officeName !== ''
      ? `${display.officeName}${display.special ? ' (special)' : ''}`
      : fallbackTitle || '—';
  const subline = display?.districtSeatLine?.trim() || '';

  return (
    <div>
      <Typography variant="subtitle1" component="div">
        {officeLine}
      </Typography>
      {subline ? (
        <Typography variant="body2" color="text.secondary" component="div">
          {subline}
        </Typography>
      ) : null}
    </div>
  );
}

function SourceRacesCell({
  entity,
  sourceElectionKey
}: {
  entity: ElectionMigrationEntity;
  sourceElectionKey: string;
}) {
  const ids = entity.sourceRaceIds || [];
  const names = entity.sourceRaceNames || [];

  return (
    <Stack spacing={0.75} alignItems="flex-start">
      {ids.map((id, i) => (
        <Link
          key={`${id}-${i}`}
          component={RouterLink}
          to={{ pathname: `/elections/${sourceElectionKey}/races/${id}` }}
          variant="caption"
          sx={{ lineHeight: 1.4 }}
          underline="always"
        >
          {names[i] || id}
        </Link>
      ))}
    </Stack>
  );
}

const ElectionMigrationRunDetail: React.FC = () => {
  const feathers = useFeathers<Application<ServiceTypes>>();
  const history = useHistory();
  const { key, runId } = useParams<{ key: string; runId?: string }>();

  const [election, setElection] = useState<Election | null>(null);
  const [run, setRun] = useState<ElectionMigrationRun | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [cancellingDiscovery, setCancellingDiscovery] = useState(false);
  const [rebuildingDataOps, setRebuildingDataOps] = useState(false);
  const [returningToReview, setReturningToReview] = useState(false);
  const [winnerPatchingUniqueId, setWinnerPatchingUniqueId] = useState<string | null>(null);
  const [expandedWinnersByEntity, setExpandedWinnersByEntity] = useState<Record<string, boolean>>({});
  const [moreMenuAnchorEl, setMoreMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [showNonMigratedModal, setShowNonMigratedModal] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [nonMigratedPage, setNonMigratedPage] = useState(0);
  const [nonMigratedRowsPerPage, setNonMigratedRowsPerPage] = useState(10);
  const [selectedMigrationEntityIds, setSelectedMigrationEntityIds] = useState<string[]>([]);
  const [filterEntityErrorsOnly, setFilterEntityErrorsOnly] = useState(false);
  const [filterWinnerWarningsOnly, setFilterWinnerWarningsOnly] = useState(false);
  const [removingRows, setRemovingRows] = useState(false);
  const [selectedNonMigratedRaceIds, setSelectedNonMigratedRaceIds] = useState<string[]>([]);
  const [includingNonMigratedRows, setIncludingNonMigratedRows] = useState(false);
  const [creatingEntities, setCreatingEntities] = useState(false);
  const [cancellingCreateEntities, setCancellingCreateEntities] = useState(false);
  const [creatingAnotherRun, setCreatingAnotherRun] = useState(false);
  const [lastCreateProgressChangeAt, setLastCreateProgressChangeAt] = useState<number | null>(null);
  const [createProgressNowMs, setCreateProgressNowMs] = useState<number>(Date.now());
  const lastObservedCreateProgressRef = useRef<number | null>(null);

  const loadRun = async (resolvedRunId: string) => {
    const runDoc = await feathers.getService('election-migration-runs').get(resolvedRunId);
    setRun(runDoc);
    return runDoc;
  };

  useEffect(() => {
    if (!feathers || !key) {
      return;
    }

    const init = async () => {
      setLoading(true);
      setError(null);
      try {
        const electionData = await feathers.getService('elections').get(key);
        setElection(electionData);

        if (runId) {
          await loadRun(runId);
        } else {
          const createdOrExisting = await feathers.getService('election-migration-runs').create({
            targetElectionKey: key
          });
          setRun(createdOrExisting);
          history.replace(`/elections/${key}/election-migration/${createdOrExisting._id}`);
        }
      } catch (err: any) {
        console.error(err);
        setError(err?.message || 'Failed to load election migration run.');
      } finally {
        setLoading(false);
      }
    };

    init();
  }, [feathers, key, runId, history]);

  useEffect(() => {
    if (!feathers || !run?._id) {
      return;
    }
    if (!run?.processInProgress?.stage || run.processInProgress.stage === 'none') {
      return;
    }

    const pollInterval = setInterval(async () => {
      try {
        await loadRun(run._id);
      } catch (err) {
        console.error('Failed to poll migration run:', err);
      }
    }, 5000);

    return () => clearInterval(pollInterval);
  }, [feathers, run?._id, run?.processInProgress?.stage]);

  const processStage = run?.processInProgress?.stage ?? 'none';
  const processActive = processStage !== 'none';

  const operationSummary = useMemo(() => {
    const dataOperations = run?.dataOperations || [];
    const completed = dataOperations.filter(op => op.status === 'completed').length;
    const failed = dataOperations.filter(op => op.status === 'failed').length;
    return {
      total: dataOperations.length,
      completed,
      failed,
      percent: dataOperations.length > 0 ? Math.round(((completed + failed) / dataOperations.length) * 100) : 0
    };
  }, [run?.dataOperations]);

  useEffect(() => {
    const isCreating = run?.processInProgress?.stage === 'creating';
    if (!isCreating) {
      lastObservedCreateProgressRef.current = null;
      setLastCreateProgressChangeAt(null);
      return;
    }
    const progressedCount = (run?.dataOperations || []).filter((op) => ['completed', 'failed'].includes(op.status)).length;
    if (lastObservedCreateProgressRef.current === null || progressedCount !== lastObservedCreateProgressRef.current) {
      lastObservedCreateProgressRef.current = progressedCount;
      setLastCreateProgressChangeAt(Date.now());
    }
  }, [run?.processInProgress?.stage, run?.dataOperations]);

  useEffect(() => {
    if (run?.processInProgress?.stage !== 'creating') {
      return;
    }
    const intervalId = setInterval(() => {
      setCreateProgressNowMs(Date.now());
    }, 5000);
    return () => clearInterval(intervalId);
  }, [run?.processInProgress?.stage]);

  const nonMigratedSourceRaces = run?.nonMigratedSourceRaces || [];
  const nonMigratedCount = nonMigratedSourceRaces.length;
  const migrationEntities = run?.migrationEntities || [];
  const migrationEntitiesWithWinnerWarnings = useMemo(
    () => migrationEntities.filter((entity) => isEntityWinnerSelectionIncomplete(entity)),
    [migrationEntities]
  );
  const filteredMigrationEntities = useMemo(
    () => migrationEntities.filter((entity) => {
      if (filterEntityErrorsOnly && !entity.error) {
        return false;
      }
      if (filterWinnerWarningsOnly && !isEntityWinnerSelectionIncomplete(entity)) {
        return false;
      }
      return true;
    }),
    [migrationEntities, filterEntityErrorsOnly, filterWinnerWarningsOnly]
  );
  const pagedMigrationEntities = useMemo(() => {
    const start = page * rowsPerPage;
    const end = start + rowsPerPage;
    return filteredMigrationEntities.slice(start, end);
  }, [filteredMigrationEntities, page, rowsPerPage]);
  const selectedMigrationEntityIdsSet = useMemo(
    () => new Set(selectedMigrationEntityIds),
    [selectedMigrationEntityIds]
  );
  const selectedNonMigratedRaceIdsSet = useMemo(
    () => new Set(selectedNonMigratedRaceIds),
    [selectedNonMigratedRaceIds]
  );
  const pagedNonMigratedSourceRaces = useMemo(() => {
    const start = nonMigratedPage * nonMigratedRowsPerPage;
    const end = start + nonMigratedRowsPerPage;
    return nonMigratedSourceRaces.slice(start, end);
  }, [nonMigratedSourceRaces, nonMigratedPage, nonMigratedRowsPerPage]);
  const migrationEntitiesWithErrors = useMemo(
    () => migrationEntities.filter((entity) => Boolean(entity.error)),
    [migrationEntities]
  );
  const hasMigrationEntityErrors = migrationEntitiesWithErrors.length > 0;
  const migrationEntityErrorMessages = useMemo(
    () => [...new Set(
      migrationEntitiesWithErrors
        .map((entity) => entity.error?.message?.trim())
        .filter((message): message is string => Boolean(message))
    )],
    [migrationEntitiesWithErrors]
  );
  const selectedFilteredMigrationEntityCount = useMemo(
    () => filteredMigrationEntities.filter((entity) => selectedMigrationEntityIdsSet.has(entity.uniqueId)).length,
    [filteredMigrationEntities, selectedMigrationEntityIdsSet]
  );

  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(filteredMigrationEntities.length / rowsPerPage) - 1);
    if (page > maxPage) {
      setPage(maxPage);
    }
  }, [filteredMigrationEntities.length, page, rowsPerPage]);
  useEffect(() => {
    const validIds = new Set(migrationEntities.map((entity) => entity.uniqueId));
    setSelectedMigrationEntityIds((prev) => {
      const next = prev.filter((id) => validIds.has(id));
      if (prev.length === next.length && prev.every((id, index) => id === next[index])) {
        return prev;
      }
      return next;
    });
  }, [migrationEntities]);
  useEffect(() => {
    setSelectedMigrationEntityIds([]);
    setPage(0);
  }, [filterEntityErrorsOnly, filterWinnerWarningsOnly]);
  useEffect(() => {
    const validIds = new Set(nonMigratedSourceRaces.map((row) => `${row.raceId}`));
    setSelectedNonMigratedRaceIds((prev) => {
      const next = prev.filter((id) => validIds.has(id));
      if (prev.length === next.length && prev.every((id, index) => id === next[index])) {
        return prev;
      }
      return next;
    });
  }, [nonMigratedSourceRaces]);
  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(nonMigratedSourceRaces.length / nonMigratedRowsPerPage) - 1);
    if (nonMigratedPage > maxPage) {
      setNonMigratedPage(maxPage);
    }
  }, [nonMigratedSourceRaces.length, nonMigratedPage, nonMigratedRowsPerPage]);

  const canEditWinners =
    !!run &&
    !['confirming', 'completed'].includes(run.status) &&
    run.processInProgress?.stage !== 'creating' &&
    run.processInProgress?.stage !== 'discovering';
  const canRemoveMigrationRows = canEditWinners;
  const allMigrationRowsSelected = filteredMigrationEntities.length > 0 && selectedFilteredMigrationEntityCount === filteredMigrationEntities.length;
  const someMigrationRowsSelected = selectedFilteredMigrationEntityCount > 0 && !allMigrationRowsSelected;
  const canIncludeNonMigratedRunoffRows = Boolean(
    run &&
    run.migrationKind === 'to-runoff' &&
    !['confirming', 'completed'].includes(run.status) &&
    run.processInProgress?.stage !== 'discovering' &&
    run.processInProgress?.stage !== 'creating'
  );
  const allNonMigratedRowsSelected = nonMigratedSourceRaces.length > 0 && selectedNonMigratedRaceIds.length === nonMigratedSourceRaces.length;
  const someNonMigratedRowsSelected = selectedNonMigratedRaceIds.length > 0 && !allNonMigratedRowsSelected;

  const handleToggleCandidate = useCallback(
    async (entity: ElectionMigrationEntity, candidateId: string) => {
      if (!feathers || !run?._id || !canEditWinners) {
        return;
      }
      const current = getSelectedCandidateIdsFromEntity(entity);
      const next = toggleWinnerSelection(entity, candidateId, current);
      setWinnerPatchingUniqueId(entity.uniqueId);
      setError(null);
      try {
        const updated = await feathers.getService('election-migration-runs').patch(run._id, {
          $updateMigrationEntityWinners: {
            uniqueId: entity.uniqueId,
            selectedCandidateIds: next
          }
        });
        setRun(updated);
      } catch (err: any) {
        console.error(err);
        setError(err?.message || 'Failed to update winner selection.');
      } finally {
        setWinnerPatchingUniqueId(null);
      }
    },
    [feathers, run, canEditWinners]
  );

  const handleConfirm = async () => {
    if (!run?._id || confirming) {
      return;
    }
    setConfirming(true);
    try {
      const updated = await feathers.getService('election-migration-runs').patch(run._id, {
        $confirmMigration: true
      });
      setRun(updated);
    } catch (err: any) {
      console.error(err);
      setError(err?.message || 'Failed to confirm migration run.');
    } finally {
      setConfirming(false);
    }
  };

  const canRefreshDiscovery =
    !!run &&
    !['confirming', 'completed'].includes(run.status) &&
    run.processInProgress?.stage !== 'discovering' &&
    run.processInProgress?.stage !== 'creating';

  const canRebuildDataOperations =
    !!run &&
    run.status !== 'completed' &&
    run.processInProgress?.stage !== 'discovering' &&
    run.processInProgress?.stage !== 'creating' &&
    (run.migrationEntities || []).length > 0;

  const canReturnToReview =
    !!run &&
    run.status === 'confirming' &&
    run.processInProgress?.stage !== 'discovering' &&
    run.processInProgress?.stage !== 'creating' &&
    (run.dataOperations || []).every(op => ['not-started', 'no-op'].includes(op.status));
  const showDataOperationsView = !!run && (
    run.status === 'confirming' ||
    run.status === 'completed' ||
    (run.status === 'failed' && (run.dataOperations || []).length > 0)
  );
  const canCreateEntities =
    !!run &&
    ['confirming', 'failed'].includes(run.status) &&
    run.processInProgress?.stage !== 'creating' &&
    (run.dataOperations || []).length > 0;
  const hasRetryableDataOperations = (run?.dataOperations || []).some((op) => (
    op.status === 'failed' || op.status === 'not-started'
  ));
  const isCreateProgressStalled = Boolean(
    run?.processInProgress?.stage === 'creating' &&
    lastCreateProgressChangeAt &&
    (createProgressNowMs - lastCreateProgressChangeAt) >= 30000
  );
  const canCancelCreateEntities = Boolean(
    run?.processInProgress?.stage === 'creating' && isCreateProgressStalled
  );

  const handleRefreshDiscovery = async () => {
    if (!run?._id || refreshing || !canRefreshDiscovery) {
      return;
    }
    setRefreshing(true);
    setError(null);
    try {
      const updated = await feathers.getService('election-migration-runs').patch(run._id, {
        $refreshDiscovery: true
      });
      setRun(updated);
    } catch (err: any) {
      console.error(err);
      setError(err?.message || 'Failed to refresh migration discovery.');
    } finally {
      setRefreshing(false);
    }
  };

  const handleCancelDiscovery = async () => {
    if (!run?._id || cancellingDiscovery || run?.processInProgress?.stage !== 'discovering') {
      return;
    }
    setCancellingDiscovery(true);
    setError(null);
    try {
      const updated = await feathers.getService('election-migration-runs').patch(run._id, {
        $cancelDiscovery: true
      });
      setRun(updated);
    } catch (err: any) {
      console.error(err);
      setError(err?.message || 'Failed to cancel migration discovery.');
    } finally {
      setCancellingDiscovery(false);
    }
  };

  const handleRebuildDataOperations = async () => {
    if (!run?._id || rebuildingDataOps || !canRebuildDataOperations) {
      return;
    }
    setRebuildingDataOps(true);
    setError(null);
    try {
      await feathers.getService('election-migration-runs').patch(run._id, {
        $rebuildDataOperations: true
      });
      await loadRun(run._id);
    } catch (err: any) {
      console.error(err);
      setError(err?.message || 'Failed to rebuild data operations.');
    } finally {
      setRebuildingDataOps(false);
    }
  };

  const handleReturnToReview = async () => {
    if (!run?._id || returningToReview || !canReturnToReview) {
      return;
    }
    setReturningToReview(true);
    setError(null);
    try {
      const updated = await feathers.getService('election-migration-runs').patch(run._id, {
        $returnToReview: true
      });
      setRun(updated);
    } catch (err: any) {
      console.error(err);
      setError(err?.message || 'Failed to return migration run to review stage.');
    } finally {
      setReturningToReview(false);
    }
  };

  const handleCreateEntities = async () => {
    if (!run?._id || !canCreateEntities || creatingEntities) {
      return;
    }
    setCreatingEntities(true);
    setError(null);
    try {
      const updated = await feathers.getService('election-migration-runs').patch(run._id, {
        $createEntities: true
      });
      setRun(updated);
    } catch (err: any) {
      console.error(err);
      setError(err?.message || 'Failed to start creating migration entities.');
    } finally {
      setCreatingEntities(false);
    }
  };

  const handleCancelCreateEntities = async () => {
    if (!run?._id || !canCancelCreateEntities || cancellingCreateEntities) {
      return;
    }
    setCancellingCreateEntities(true);
    setError(null);
    try {
      const updated = await feathers.getService('election-migration-runs').patch(run._id, {
        $cancelCreateEntities: true
      });
      setRun(updated);
    } catch (err: any) {
      console.error(err);
      setError(err?.message || 'Failed to cancel create processing.');
    } finally {
      setCancellingCreateEntities(false);
    }
  };

  const handleMigrateMoreRaces = async () => {
    if (!key || creatingAnotherRun) {
      return;
    }
    setCreatingAnotherRun(true);
    setError(null);
    try {
      const newRun = await feathers.getService('election-migration-runs').create({
        targetElectionKey: key,
        forceNewRun: true
      });
      history.push(`/elections/${key}/election-migration/${newRun._id}`);
    } catch (err: any) {
      console.error(err);
      setError(err?.message || 'Failed to create a new migration run.');
    } finally {
      setCreatingAnotherRun(false);
    }
  };

  const handleToggleMigrationRowSelection = (uniqueId: string) => {
    setSelectedMigrationEntityIds((prev) => {
      if (prev.includes(uniqueId)) {
        return prev.filter((id) => id !== uniqueId);
      }
      return [...prev, uniqueId];
    });
  };

  const handleToggleSelectAllMigrationRows = () => {
    if (allMigrationRowsSelected) {
      setSelectedMigrationEntityIds([]);
      return;
    }
    setSelectedMigrationEntityIds(filteredMigrationEntities.map((entity) => entity.uniqueId));
  };

  const handleRemoveSelectedFromMigration = async () => {
    if (!run?._id || !canRemoveMigrationRows || removingRows || selectedMigrationEntityIds.length === 0) {
      return;
    }
    setRemovingRows(true);
    setError(null);
    try {
      const updated = await feathers.getService('election-migration-runs').patch(run._id, {
        $removeMigrationEntities: {
          uniqueIds: selectedMigrationEntityIds
        }
      });
      setRun(updated);
      setSelectedMigrationEntityIds([]);
    } catch (err: any) {
      console.error(err);
      setError(err?.message || 'Failed to remove selected rows from migration.');
    } finally {
      setRemovingRows(false);
    }
  };

  const handleToggleNonMigratedRowSelection = (raceId: string) => {
    setSelectedNonMigratedRaceIds((prev) => {
      if (prev.includes(raceId)) {
        return prev.filter((id) => id !== raceId);
      }
      return [...prev, raceId];
    });
  };

  const handleToggleSelectAllNonMigratedRows = () => {
    if (allNonMigratedRowsSelected) {
      setSelectedNonMigratedRaceIds([]);
      return;
    }
    setSelectedNonMigratedRaceIds(nonMigratedSourceRaces.map((row) => `${row.raceId}`));
  };

  const handleIncludeSelectedRunoffRaces = async () => {
    if (!run?._id || !canIncludeNonMigratedRunoffRows || includingNonMigratedRows || selectedNonMigratedRaceIds.length === 0) {
      return;
    }
    setIncludingNonMigratedRows(true);
    setError(null);
    try {
      const updated = await feathers.getService('election-migration-runs').patch(run._id, {
        $includeRunoffRacesInMigration: {
          raceIds: selectedNonMigratedRaceIds
        }
      });
      setRun(updated);
      setSelectedNonMigratedRaceIds([]);
      setShowNonMigratedModal(false);
    } catch (err: any) {
      console.error(err);
      setError(err?.message || 'Failed to include selected runoff races in migration.');
    } finally {
      setIncludingNonMigratedRows(false);
    }
  };

  if (loading) {
    return (
      <Wrapper>
        <LoadingSpinner width={0.8} />
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <DataDetailToolbar
        onBack={{ to: { pathname: `/elections/${key}` } }}
        navTree={[
          { text: 'Elections', to: { pathname: '/elections' } },
          { text: election?.name || key, to: { pathname: `/elections/${key}` } },
          { text: 'Election migration' }
        ]}
        title="Election Migration"
        actionButtonsComponent={
          <Stack direction="row" spacing={0.5} alignItems="center">
            <IconButton
              aria-label="More actions"
              aria-controls={moreMenuAnchorEl ? 'election-migration-more-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={moreMenuAnchorEl ? 'true' : undefined}
              onClick={(event) => setMoreMenuAnchorEl(event.currentTarget)}
              disabled={!run}
              color="primary"
              size="medium"
            >
              <MoreVertIcon />
            </IconButton>
            <Menu
              id="election-migration-more-menu"
              anchorEl={moreMenuAnchorEl}
              open={Boolean(moreMenuAnchorEl)}
              onClose={() => setMoreMenuAnchorEl(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <MenuItem
                disabled={!canRefreshDiscovery || refreshing}
                onClick={() => {
                  setMoreMenuAnchorEl(null);
                  void handleRefreshDiscovery();
                }}
              >
                {refreshing ? 'Refreshing…' : 'Refresh discovery'}
              </MenuItem>
              <MenuItem
                disabled={!canRebuildDataOperations || rebuildingDataOps}
                onClick={() => {
                  setMoreMenuAnchorEl(null);
                  void handleRebuildDataOperations();
                }}
              >
                {rebuildingDataOps ? 'Rebuilding…' : 'Rebuild data operations'}
              </MenuItem>
              <MenuItem
                disabled={!canReturnToReview || returningToReview}
                onClick={() => {
                  setMoreMenuAnchorEl(null);
                  void handleReturnToReview();
                }}
              >
                {returningToReview ? 'Returning…' : 'Back to review/edit'}
              </MenuItem>
              {run?.status === 'completed' && (
                <MenuItem
                  disabled={creatingAnotherRun}
                  onClick={() => {
                    setMoreMenuAnchorEl(null);
                    void handleMigrateMoreRaces();
                  }}
                >
                  {creatingAnotherRun ? 'Starting…' : 'Migrate more races'}
                </MenuItem>
              )}
            </Menu>
            {!showDataOperationsView && (
              <Button
                variant="contained"
                color="primary"
                onClick={handleConfirm}
                disabled={!run || run.status !== 'ready' || confirming || hasMigrationEntityErrors}
              >
                Confirm
              </Button>
            )}
          </Stack>
        }
      />
      <Content>
        {error && <Alert severity="error">{error}</Alert>}
        {run?.status === 'failed' && (
          <Alert severity="error">{run?.error?.message || 'Migration run failed.'}</Alert>
        )}
        {!showDataOperationsView && hasMigrationEntityErrors && (
          <Alert severity="error">
            Resolve {migrationEntitiesWithErrors.length} migration row error{migrationEntitiesWithErrors.length === 1 ? '' : 's'} before confirming.
            {migrationEntityErrorMessages.length > 0 ? ` ${migrationEntityErrorMessages[0]}` : ''}
          </Alert>
        )}

        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} flexWrap="wrap">
          <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
            <Chip label={`Status: ${run?.status || '--'}`} size="small" />
            <Chip
              size="small"
              icon={
                processActive ? (
                  <CircularProgress color="inherit" size={14} thickness={5} aria-label="Process in progress" />
                ) : undefined
              }
              label={`Process: ${processStage}`}
            />
            <Chip label={`Kind: ${run?.migrationKind || '--'}`} size="small" />
          </Stack>
          {!showDataOperationsView && (
            <Stack direction="row" spacing={1} alignItems="center">
              {migrationEntitiesWithErrors.length > 0 && (
                <Button
                  variant="outlined"
                  color="inherit"
                  size="small"
                  onClick={() => setFilterEntityErrorsOnly((prev) => !prev)}
                  sx={{
                    textTransform: 'none',
                    borderRadius: '9999px',
                    borderWidth: '1px',
                    borderStyle: 'solid',
                    borderColor: 'divider',
                    backgroundColor: filterEntityErrorsOnly ? 'primary.main' : 'common.white',
                    color: filterEntityErrorsOnly ? 'primary.contrastText' : 'text.primary',
                    '&:hover': {
                      borderColor: 'primary.main',
                      backgroundColor: filterEntityErrorsOnly ? 'primary.dark' : 'primary.light',
                      color: filterEntityErrorsOnly ? 'primary.contrastText' : 'text.primary'
                    }
                  }}
                >
                  Errors ({migrationEntitiesWithErrors.length})
                </Button>
              )}
              <Button
                variant="outlined"
                color="inherit"
                size="small"
                onClick={() => setFilterWinnerWarningsOnly((prev) => !prev)}
                sx={{
                  textTransform: 'none',
                  borderRadius: '9999px',
                  borderWidth: '1px',
                  borderStyle: 'solid',
                  borderColor: 'divider',
                  backgroundColor: filterWinnerWarningsOnly ? 'primary.main' : 'common.white',
                  color: filterWinnerWarningsOnly ? 'primary.contrastText' : 'text.primary',
                  '&:hover': {
                    borderColor: 'primary.main',
                    backgroundColor: filterWinnerWarningsOnly ? 'primary.dark' : 'primary.light',
                    color: filterWinnerWarningsOnly ? 'primary.contrastText' : 'text.primary'
                  }
                }}
              >
                Missing winners ({migrationEntitiesWithWinnerWarnings.length})
              </Button>
            </Stack>
          )}
        </Stack>

        {run?.processInProgress?.stage === 'creating' && (
          <div>
            <Typography variant="body2" style={{ marginBottom: 8 }}>
              Processing create operations: {operationSummary.completed + operationSummary.failed} / {operationSummary.total}
            </Typography>
            <LinearProgress variant="determinate" value={operationSummary.percent} />
            {canCancelCreateEntities && (
              <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  color="warning"
                  onClick={handleCancelCreateEntities}
                  disabled={cancellingCreateEntities}
                >
                  {cancellingCreateEntities ? 'Cancelling…' : 'Cancel processing'}
                </Button>
              </div>
            )}
          </div>
        )}

        {!showDataOperationsView && selectedMigrationEntityIds.length > 0 && (
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              variant="outlined"
              color="warning"
              onClick={handleRemoveSelectedFromMigration}
              disabled={!canRemoveMigrationRows || removingRows}
            >
              {removingRows ? 'Removing…' : 'Remove from migration'}
            </Button>
            <Typography variant="body2" color="text.secondary">
              {selectedFilteredMigrationEntityCount} selected
            </Typography>
          </Stack>
        )}

        {showDataOperationsView ? (
          <>
            <DataOperationsTable
              dataOperations={(run?.dataOperations || []).map(toTableDataOperation)}
              title="Migration Data Operations"
              electionKey={key}
              editingDisabled={run?.processInProgress?.stage === 'creating'}
              onChangeDataOperations={async (operations) => {
                if (!run?._id) {
                  return;
                }
                try {
                  setError(null);
                  const updated = await feathers.getService('election-migration-runs').patch(run._id, {
                    dataOperations: operations.map(toElectionMigrationDataOperation)
                  });
                  setRun(updated);
                } catch (err: any) {
                  console.error(err);
                  setError(err?.message || 'Failed to save data operations.');
                }
              }}
              onRetryFailedOperations={
                run?.processInProgress?.stage !== 'creating' &&
                hasRetryableDataOperations
                  ? () => { void handleCreateEntities(); }
                  : undefined
              }
            />
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '8px' }}>
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={handleCreateEntities}
                disabled={!canCreateEntities || creatingEntities}
              >
                {run?.status === 'failed' ? 'Retry Create' : 'Create'}
              </Button>
            </div>
          </>
        ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    checked={allMigrationRowsSelected}
                    indeterminate={someMigrationRowsSelected}
                    onChange={handleToggleSelectAllMigrationRows}
                    inputProps={{ 'aria-label': 'Select all migration rows' }}
                    disabled={filteredMigrationEntities.length === 0 || !canRemoveMigrationRows}
                  />
                </TableCell>
                <TableCell><Typography variant="h5">Target race</Typography></TableCell>
                <TableCell><Typography variant="h5">Plurality</Typography></TableCell>
                <TableCell><Typography variant="h5">Source races</Typography></TableCell>
                <TableCell><Typography variant="h5">Candidates to migrate</Typography></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredMigrationEntities.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography variant="body1" style={{ padding: '16px 0' }}>
                      {migrationEntities.length === 0 && run?.processInProgress?.stage === 'discovering'
                        ? 'Discovering races to migrate...'
                        : migrationEntities.length === 0
                          ? 'No migration entities found for this run.'
                          : 'No migration rows match the selected filters.'}
                    </Typography>
                    {migrationEntities.length === 0 && run?.processInProgress?.stage === 'discovering' && (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          void handleCancelDiscovery();
                        }}
                        disabled={cancellingDiscovery}
                      >
                        {cancellingDiscovery ? 'Cancelling…' : 'Cancel'}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ) : (
                pagedMigrationEntities.map((entity) => (
                  <TableRow key={entity.uniqueId}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={selectedMigrationEntityIdsSet.has(entity.uniqueId)}
                        onChange={() => handleToggleMigrationRowSelection(entity.uniqueId)}
                        inputProps={{ 'aria-label': `Select migration row ${entity.uniqueId}` }}
                        disabled={!canRemoveMigrationRows}
                      />
                    </TableCell>
                    <TableCell>
                      <Stack spacing={0.5} alignItems="flex-start">
                        <TargetRaceInfoCell
                          display={entity.targetRaceDisplay}
                          fallbackTitle={entity.sourceRaceNames?.[0]}
                        />
                        {entity.error?.message ? (
                          <Typography variant="caption" color="error">
                            {entity.error.message}
                          </Typography>
                        ) : null}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {entity.plurality ?? entity.createPayload?.maxChoices ?? '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {run?.sourceElectionKey ? (
                        <SourceRacesCell entity={entity} sourceElectionKey={run.sourceElectionKey} />
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          {(entity.sourceRaceNames || []).join(', ')}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <WinnersPickerCell
                        entity={entity}
                        canEdit={canEditWinners}
                        patching={winnerPatchingUniqueId === entity.uniqueId}
                        expanded={Boolean(expandedWinnersByEntity[entity.uniqueId])}
                        onToggleExpand={() => {
                          setExpandedWinnersByEntity((prev) => ({
                            ...prev,
                            [entity.uniqueId]: !prev[entity.uniqueId]
                          }));
                        }}
                        onToggleCandidate={(cid) => {
                          void handleToggleCandidate(entity, cid);
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          {filteredMigrationEntities.length > 0 && (
            <TablePagination
              component="div"
              count={filteredMigrationEntities.length}
              page={page}
              onPageChange={(_, nextPage) => setPage(nextPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(parseInt(event.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          )}
        </TableContainer>
        )}

        {!showDataOperationsView && nonMigratedCount > 0 && (
          <Alert
            style={{ maxWidth: '500px' }}
            severity="info"
            action={(
              <Button
                color="inherit"
                size="small"
                onClick={() => {
                  setNonMigratedPage(0);
                  setSelectedNonMigratedRaceIds([]);
                  setShowNonMigratedModal(true);
                }}
              >
                View races
              </Button>
            )}
          >
            {nonMigratedCount} race{nonMigratedCount === 1 ? '' : 's'} won't be migrated.
          </Alert>
        )}
      </Content>
      <Dialog
        open={showNonMigratedModal}
        onClose={() => setShowNonMigratedModal(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Races Not Included In Migration</DialogTitle>
        <DialogContent dividers>
          {run?.migrationKind === 'to-runoff' && selectedNonMigratedRaceIds.length > 0 && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleIncludeSelectedRunoffRaces}
                disabled={!canIncludeNonMigratedRunoffRows || includingNonMigratedRows}
              >
                {includingNonMigratedRows ? 'Adding…' : 'Add to migration'}
              </Button>
              <Typography variant="body2" color="text.secondary">
                {selectedNonMigratedRaceIds.length} selected
              </Typography>
            </Stack>
          )}
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    checked={allNonMigratedRowsSelected}
                    indeterminate={someNonMigratedRowsSelected}
                    onChange={handleToggleSelectAllNonMigratedRows}
                    inputProps={{ 'aria-label': 'Select all non-migrated rows' }}
                    disabled={!canIncludeNonMigratedRunoffRows || nonMigratedSourceRaces.length === 0}
                  />
                </TableCell>
                <TableCell><Typography variant="h6">Race</Typography></TableCell>
                <TableCell><Typography variant="h6">Candidates</Typography></TableCell>
                <TableCell><Typography variant="h6">Open race</Typography></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pagedNonMigratedSourceRaces.map((row) => {
                const names = row.candidateSummary?.names || [];
                const parties = row.candidateSummary?.parties || [];
                const visible = names.slice(0, MAX_MODAL_CANDIDATES_VISIBLE);
                const remaining = Math.max(0, names.length - visible.length);
                return (
                <TableRow key={row.raceId}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={selectedNonMigratedRaceIdsSet.has(`${row.raceId}`)}
                      onChange={() => handleToggleNonMigratedRowSelection(`${row.raceId}`)}
                      inputProps={{ 'aria-label': `Select non-migrated row ${row.raceId}` }}
                      disabled={!canIncludeNonMigratedRunoffRows}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{row.raceName || row.raceId}</Typography>
                  </TableCell>
                  <TableCell>
                    {names.length > 0 ? (
                      <Stack spacing={0.5} alignItems="flex-start">
                        {visible.map((name, index) => (
                          <Typography key={`${row.raceId}-cand-${index}`} variant="caption">
                            {name}{parties[index] ? ` (${parties[index]})` : ''}
                          </Typography>
                        ))}
                        {remaining > 0 ? (
                          <Typography variant="caption" color="text.secondary">
                            +{remaining} more
                          </Typography>
                        ) : null}
                      </Stack>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        No candidates
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {run?.sourceElectionKey ? (
                      <Link
                        component={RouterLink}
                        to={{ pathname: `/elections/${run.sourceElectionKey}/races/${row.raceId}` }}
                        underline="always"
                        variant="body2"
                      >
                        Open race
                      </Link>
                    ) : (
                      <Typography variant="body2" color="text.secondary">—</Typography>
                    )}
                  </TableCell>
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
          {nonMigratedCount > 0 && (
            <TablePagination
              component="div"
              count={nonMigratedCount}
              page={nonMigratedPage}
              onPageChange={(_, nextPage) => setNonMigratedPage(nextPage)}
              rowsPerPage={nonMigratedRowsPerPage}
              onRowsPerPageChange={(event) => {
                setNonMigratedRowsPerPage(parseInt(event.target.value, 10));
                setNonMigratedPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50]}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowNonMigratedModal(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Wrapper>
  );
};

export default ElectionMigrationRunDetail;
